import React from 'react';
import { useNavigate, useParams } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { DeleteEventButton } from '@/components/org/DeleteEventButton';
import { Loader2, Edit, Trash2, AlertCircle, Calendar } from 'lucide-react';
import { formatBritishTime } from '@/lib/date';
import { useOrgContext } from '@/contexts/OrgContext';
import { MarkdownRender } from '@/components/ui/markdown-render';
import { PageTopNav } from '@/components/nav/PageTopNav';

export function OrgEventDetail() {
  const { eventId, orgId } = useParams();
  const queryClient = useQueryClient();
  const { orgRole } = useOrgContext();
  const navigate = useNavigate();

  const {
    data: event,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['eventDetail', eventId],
    queryFn: async () => {
      const response = await fetch(`/api/org/${orgId}/event/${eventId}`);
      if (!response.ok) throw new Error('Failed to fetch event');
      return response.json();
    },
    enabled: !!eventId,
  });


  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading event...</span>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="text-center text-destructive p-4">
        <AlertCircle className="h-8 w-8 mx-auto mb-2" />
        <p>Error loading event. Please try again later.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto">
      <PageTopNav pageName='Event Details'
        rightItem={
          <div>
            {orgRole === "ADMIN" && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    if (orgId && eventId) {
                      navigate(`/app/org/${orgId}/events/${eventId}/edit`);
                    }
                  }}
                >
                  <Edit className="h-4 w-4" />
                  <span className="hidden md:inline">Edit</span>
                </Button>
                <DeleteEventButton eventId={eventId} orgId={orgId} />
              </div>
            )}
          </div>
        }
      />
      <div className="flex flex-col justify-between items-center gap-2 mb-2">
        <div className="text-muted-foreground mb-4">
          {formatBritishTime(event.startDate)}
        </div>
        <MarkdownRender content={event.description} className="mb-6" />
      </div>
    </div>
  );
}

export default OrgEventDetail;
